/**
 * ContactForm
 * Rôle:
 * - Proposer un formulaire de contact simple branché sur EmailJS via `sendContact`.
 *
 * Responsabilités principales:
 * - Gérer les champs nom, email, message et les états de validation/envoi.
 * - Afficher un feedback utilisateur clair en cas de succès ou d’erreur.
 *
 * Évolutions attendues:
 * - Externaliser les textes dans un fichier de contenu et ajouter plus de champs (objet, type de demande).
 * - Intégrer une solution de rate limiting / captcha si nécessaire.
 */
import { useState } from 'react'
import { sendContact } from '@/lib/email'

type Props = {
  variant?: 'default' | 'lite'
}

export default function ContactForm({ variant = 'default' }: Props) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'ok' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)
  const isLite = variant === 'lite'

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name || !email || !message) {
      setError('Veuillez remplir tous les champs.')
      return
    }
    setStatus('sending')
    try {
      await sendContact({ name, email, message })
      setStatus('ok')
      setName('')
      setEmail('')
      setMessage('')
    } catch {
      setStatus('error')
      setError('Échec de l’envoi. Vérifiez la configuration EmailJS.')
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className={
        isLite
          ? 'lite-card space-y-4 rounded-2xl p-5'
          : 'space-y-3 rounded-lg border border-slate-800 bg-slate-900/60 p-4'
      }
    >
      <div className={isLite ? 'text-xs tracking-widest uppercase text-slate-400' : 'text-sm text-slate-400'}>
        Contact
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Votre nom"
          className={
            isLite
              ? 'rounded-xl border border-slate-700 bg-slate-950/25 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-brand-500/50'
              : 'rounded bg-slate-800 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand-500'
          }
        />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Votre email"
          type="email"
          className={
            isLite
              ? 'rounded-xl border border-slate-700 bg-slate-950/25 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-brand-500/50'
              : 'rounded bg-slate-800 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand-500'
          }
        />
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Votre message"
        rows={5}
        className={
          isLite
            ? 'w-full rounded-xl border border-slate-700 bg-slate-950/25 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-brand-500/50'
            : 'w-full rounded bg-slate-800 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-brand-500'
        }
      />
      {error && <div className="text-sm text-red-400">{error}</div>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={status === 'sending'}
          className={
            isLite
              ? 'rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-brand-400 disabled:opacity-60 transition-colors'
              : 'rounded bg-brand-500 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-brand-400 disabled:opacity-60'
          }
        >
          {status === 'sending' ? 'Envoi…' : 'Envoyer'}
        </button>
        {status === 'ok' && <span className="text-sm text-green-400">Message envoyé ✔</span>}
        {status === 'error' && <span className="text-sm text-red-400">Erreur ❌</span>}
      </div>
    </form>
  )
}


